/**
 * src/components/landing/BuscadorDocumentos.tsx
 * =============================================================================
 * Campo de búsqueda del panel de documentos.
 *
 * Existe porque el panel se rompe justo cuando el producto empieza a funcionar:
 * un estudio no tiene tres documentos, tiene doscientos, y sin buscador la
 * única forma de llegar al de "casa Ana" es bajar con el ratón.
 *
 * DOS DECISIONES QUE NO SE VEN
 * ----------------------------
 * 1. Espera 250 ms antes de consultar. Sin eso, escribir "casa ana" son ocho
 *    consultas a una base que atiende de una en una: el buscador de una persona
 *    ralentizaría a todas las demás.
 *
 * 2. El campo solo aparece cuando hay bastantes documentos como para que haga
 *    falta. A quien tiene cuatro, un buscador no le ayuda: le ocupa sitio y le
 *    sugiere que aquí hay algo complicado que aprender.
 *
 * Vive aparte porque MisDocumentosDrawer ya pasa de 600 líneas y no conviene
 * seguir engordándolo.
 * =============================================================================
 */

import { useEffect, useState } from 'react'
import { Search, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

/** A partir de cuántos documentos merece la pena enseñar el buscador. */
export const MINIMO_PARA_BUSCAR = 8

interface Props {
  /** Se llama con el texto ya reposado, listo para consultar. */
  onBuscar: (q: string) => void
  /** Si hay una búsqueda en curso (para no confundir con "no hay nada"). */
  buscando?: boolean
}

export function BuscadorDocumentos({ onBuscar, buscando }: Props) {
  const { t } = useTranslation()
  const [texto, setTexto] = useState('')

  useEffect(() => {
    // Ojo: no llamar `t` a este temporizador. La traducción también se llama
    // `t` y quedaría tapada dentro de este efecto — legal en JavaScript y una
    // trampa para el que lo lea dentro de seis meses.
    const temporizador = window.setTimeout(() => onBuscar(texto.trim()), 250)
    return () => window.clearTimeout(temporizador)
  }, [texto, onBuscar])

  return (
    <div className="relative mb-2">
      <Search
        className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-400 pointer-events-none"
        strokeWidth={1.8}
      />
      <input
        type="search"
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        placeholder={t('panel.buscar')}
        aria-label={t('panel.buscarEtiqueta')}
        className="w-full h-9 pl-8 pr-8 rounded-lg border border-zinc-200 bg-white text-xs text-zinc-700 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-300"
      />
      {texto && (
        <button
          type="button"
          onClick={() => setTexto('')}
          aria-label={t('panel.limpiar')}
          className="absolute right-2 top-1/2 -translate-y-1/2 h-5 w-5 flex items-center justify-center rounded text-zinc-400 hover:text-zinc-700"
        >
          <X className="h-3.5 w-3.5" strokeWidth={1.8} />
        </button>
      )}
      {buscando && texto && (
        <span className="sr-only" role="status">
          {t('panel.buscar')}
        </span>
      )}
    </div>
  )
}
